import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEOMeta from "@/components/base/SEOMeta";
import { categories, getArticlesByCategory } from "@/mocks/articles";

const categoryImages: Record<string, string> = {
  family: "https://readdy.ai/api/search-image?query=A%20warm%20modern%20family%20living%20room%20with%20security%20system%20details%2C%20soft%20natural%20light%20through%20large%20windows%2C%20cozy%20protective%20atmosphere%2C%20clean%20contemporary%20interior%20design%2C%20professional%20architectural%20photography%2C%20warm%20neutral%20color%20palette&width=1200&height=400&seq=cat-family-header&orientation=landscape",
  business: "https://readdy.ai/api/search-image?query=A%20modern%20corporate%20office%20building%20with%20glass%20facade%20and%20security%20access%20gates%2C%20professional%20business%20atmosphere%2C%20clean%20minimalist%20architecture%2C%20bright%20daylight%2C%20corporate%20security%20concept%20photography%2C%20high%20quality%20urban%20landscape&width=1200&height=400&seq=cat-business-header&orientation=landscape",
  personal: "https://readdy.ai/api/search-image?query=A%20minimalist%20clean%20desk%20setup%20with%20personal%20safety%20devices%20and%20compact%20security%20tools%20arranged%20neatly%2C%20soft%20natural%20lighting%2C%20modern%20lifestyle%20photography%2C%20professional%20product%20display%2C%20warm%20neutral%20aesthetic%20background&width=1200&height=400&seq=cat-personal-header&orientation=landscape",
};

const articleCardImages: Record<string, string> = {
  "home-security-guide-2026": "https://readdy.ai/api/search-image?query=A%20professional%20modern%20minimalist%20scene%20related%20to%20security%20and%20safety%20concept%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20warm%20neutral%20tones%2C%20editorial%20photography%20style%2C%20high%20detail%2C%20professional%20atmosphere&width=600&height=350&seq=article-home-guide&orientation=landscape",
  "business-security-risk-assessment": "https://readdy.ai/api/search-image?query=A%20modern%20corporate%20meeting%20room%20with%20security%20presentation%20on%20screen%2C%20professional%20business%20setting%2C%20clean%20office%20environment%2C%20soft%20lighting%2C%20corporate%20atmosphere%2C%20editorial%20photography%20style%2C%20high%20detail&width=600&height=350&seq=article-business-risk&orientation=landscape",
  "personal-safety-essentials": "https://readdy.ai/api/search-image?query=A%20minimalist%20desk%20with%20personal%20safety%20devices%20arranged%20neatly%2C%20compact%20alarm%20tools%2C%20modern%20lifestyle%20photography%2C%20soft%20window%20light%2C%20clean%20neutral%20background%2C%20professional%20product%20display&width=600&height=350&seq=article-personal-safety&orientation=landscape",
  "smart-home-security-trends": "https://readdy.ai/api/search-image?query=A%20futuristic%20smart%20home%20with%20AI%20security%20interface%20hologram%2C%20modern%20living%20room%2C%20clean%20design%2C%20soft%20ambient%20lighting%2C%20technology%20concept%2C%20editorial%20photography%2C%20high%20detail%2C%20warm%20tones&width=600&height=350&seq=article-smart-trends&orientation=landscape",
  "office-fire-safety-guide": "https://readdy.ai/api/search-image?query=A%20modern%20office%20with%20fire%20safety%20equipment%20displayed%20professionally%2C%20clean%20corporate%20environment%2C%20safety%20signage%2C%20bright%20natural%20lighting%2C%20professional%20photography%20style%2C%20warm%20neutral%20atmosphere&width=600&height=350&seq=article-fire-safety&orientation=landscape",
  "night-walk-safety-tips": "https://readdy.ai/api/search-image?query=A%20safe%20well%20lit%20city%20street%20at%20night%20with%20streetlamps%2C%20modern%20urban%20setting%2C%20warm%20lighting%2C%20clean%20cityscape%2C%20safety%20concept%2C%20editorial%20photography%2C%20professional%20atmosphere%2C%20high%20detail&width=600&height=350&seq=article-night-safety&orientation=landscape",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${category.name} - 安大师安全防护解决方案`}
        description={`安大师${category.name}服务 - ${category.description}`}
        keywords={`安大师,${category.name},安全防护`}
        canonicalUrl={`https://www.anmaster.com/category/${slug}`}
      />
      <Navbar />

      <main>
        <div className="relative h-56 md:h-72 overflow-hidden">
          <img
            src={categoryImages[slug] || categoryImages["family"]}
            alt={category.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pt-16">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: category.name },
                ]}
              />
              <div className="flex items-center gap-3 mt-4">
                <div className="w-10 h-10 bg-primary-500/90 rounded-lg flex items-center justify-center">
                  <i className={`${category.icon} text-lg text-background-50`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
                    {category.name}
                  </h1>
                  <p className="text-sm text-background-50/70 mt-1">{category.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-50 py-4 border-b border-background-200/50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl font-bold text-foreground-900 font-heading mb-8">
              {category.name}相关文章
            </h2>

            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {articles.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/50 overflow-hidden transition-all duration-300 hover:border-primary-300/50"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={articleCardImages[article.slug] || articleCardImages["home-security-guide-2026"]}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-background-200/50">
                        <span className="text-xs text-primary-600 font-medium whitespace-nowrap">阅读全文</span>
                        <i className="ri-arrow-right-line text-xs text-primary-500 group-hover:translate-x-1 transition-transform duration-200"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-article-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}